import React, { useEffect, useMemo, useState } from "react";
import {
  collectionGroup,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
} from "firebase/firestore";
import { dbLOA } from "../../../../backend/firebaseConfig";
import "../../../../css/Praplanning/ParaplanningCasesTable.css";

const FETCH_LIMIT = 500;
const CHARGE_FOUND_THRESHOLD = 14;
const CHARGE_PER_SCHEME = 1;
const FREE_ANALYSES_COUNT = 10;

const normalise = (value) => (value || "").toString().trim().toLowerCase();

const getTimestampMs = (value) => {
  if (!value) return 0;
  if (typeof value === "number") return value;
  if (value?.toDate) return value.toDate().getTime();

  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

const getCreatedMs = (item) =>
  getTimestampMs(item.createdAtMs) ||
  getTimestampMs(item.createdAt) ||
  getTimestampMs(item.updatedAtMs) ||
  getTimestampMs(item.updatedAt) ||
  0;

const formatDate = (ms) => {
  if (!ms) return "—";

  return new Date(ms).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getMonthLabel = (ms) => {
  if (!ms) return "Unknown";

  return new Date(ms).toLocaleDateString("en-GB", {
    month: "long",
    year: "numeric",
  });
};

const getCountsFromAnswers = (answers = []) => {
  return answers.reduce(
    (acc, row) => {
      const status = normalise(row?.status);

      if (status === "found") acc.found += 1;
      else if (status === "partial") acc.partial += 1;
      else acc.missing += 1;

      return acc;
    },
    {
      found: 0,
      partial: 0,
      missing: 0,
    },
  );
};

const hasRealCounts = (counts) => {
  return (
    counts &&
    ((counts.found || 0) > 0 ||
      (counts.partial || 0) > 0 ||
      (counts.missing || 0) > 0)
  );
};

const getItemCounts = (item = {}) => {
  if (hasRealCounts(item.counts)) {
    return {
      found: item.counts.found || 0,
      partial: item.counts.partial || 0,
      missing: item.counts.missing || 0,
    };
  }

  return getCountsFromAnswers(
    item.extractionJson?.answers || item.answers || [],
  );
};

const getSchemeCounts = (scheme = {}) => {
  if (hasRealCounts(scheme.counts)) {
    return {
      found: scheme.counts.found || 0,
      partial: scheme.counts.partial || 0,
      missing: scheme.counts.missing || 0,
    };
  }

  return getCountsFromAnswers(
    scheme.extractionJson?.answers || scheme.answers || [],
  );
};

const getAnswerValue = (item, question) => {
  const answers = item?.extractionJson?.answers || item?.answers || [];

  return (
    answers.find((answer) => answer.question === question)?.answer ||
    answers.find((answer) => normalise(answer.question) === normalise(question))
      ?.answer ||
    ""
  );
};

const getClientName = (item) =>
  item?.summary?.clientName ||
  item?.extractionJson?.summary?.clientName ||
  getAnswerValue(item, "Client's Name") ||
  "Client not found";

const getProviderName = (item) =>
  item?.summary?.providerName ||
  item?.extractionJson?.summary?.providerName ||
  getAnswerValue(item, "Pension Provider Name") ||
  item?.name ||
  "Provider not found";

const getPolicyNumber = (item) =>
  item?.summary?.policyNumber ||
  item?.extractionJson?.summary?.policyNumber ||
  getAnswerValue(item, "Policy Number or Plan Number") ||
  "";

const getSchemeCharge = (foundCount) =>
  Number(foundCount || 0) > CHARGE_FOUND_THRESHOLD ? CHARGE_PER_SCHEME : 0;

const shouldIncludeExtraction = (item = {}) => {
  if (item.status === "completed") return true;

  if (Array.isArray(item.schemes) && item.schemes.length > 0) {
    return item.schemes.some(
      (scheme) =>
        scheme.extractionJson ||
        Array.isArray(scheme.answers) ||
        hasRealCounts(scheme.counts),
    );
  }

  return (
    item.extractionJson ||
    Array.isArray(item.answers) ||
    hasRealCounts(item.counts) ||
    item.summary
  );
};

const fetchAllExtractions = async () => {
  const allDocs = [];
  let lastDoc = null;
  let hasMore = true;

  while (hasMore) {
    const q = lastDoc
      ? query(
          collectionGroup(dbLOA, "Extractions"),
          orderBy("createdAt", "desc"),
          startAfter(lastDoc),
          limit(FETCH_LIMIT),
        )
      : query(
          collectionGroup(dbLOA, "Extractions"),
          orderBy("createdAt", "desc"),
          limit(FETCH_LIMIT),
        );

    const snapshot = await getDocs(q);

    allDocs.push(...snapshot.docs);

    lastDoc = snapshot.docs[snapshot.docs.length - 1] || null;
    hasMore = snapshot.docs.length === FETCH_LIMIT;
  }

  return allDocs.map((docSnap) => ({
    id: docSnap.id,
    ownerEmail: normalise(docSnap.ref?.parent?.parent?.id),
    ...docSnap.data(),
  }));
};

const buildSchemeRowsFromExtraction = (extraction) => {
  const createdMs = getCreatedMs(extraction);

  const ownerEmail =
    normalise(extraction.ownerEmail) ||
    normalise(extraction.adviserEmail) ||
    normalise(extraction.userEmail) ||
    normalise(extraction.createdBy) ||
    "unknown";

  const base = {
    extractionId: extraction.id,
    createdMs,
    month: getMonthLabel(createdMs),
    ownerEmail,
    createdBy: extraction.createdBy || extraction.userEmail || ownerEmail,
    status: extraction.status || "completed",
  };

  if (Array.isArray(extraction.schemes) && extraction.schemes.length > 0) {
    return extraction.schemes.map((scheme, schemeIndex) => {
      const counts = getSchemeCounts(scheme);
      const found = counts.found || 0;
      const charge = getSchemeCharge(found);

      const clientName =
        getClientName(scheme) !== "Client not found"
          ? getClientName(scheme)
          : getClientName(extraction);

      return {
        ...base,
        id: `${ownerEmail}-${extraction.id}-${schemeIndex}`,
        clientName,
        found,
        charge,
        chargeable: charge > 0,
      };
    });
  }

  const counts = getItemCounts(extraction);
  const found = counts.found || 0;
  const charge = getSchemeCharge(found);

  return [
    {
      ...base,
      id: `${ownerEmail}-${extraction.id}-single`,
      clientName: getClientName(extraction),
      found,
      charge,
      chargeable: charge > 0,
    },
  ];
};

const ProCedingAdviserTab = ({ adviserEmail }) => {
  const [extractions, setExtractions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    const loadExtractions = async () => {
      try {
        setLoading(true);
        setError("");

        const loaded = await fetchAllExtractions();

        const completed = loaded
          .filter(shouldIncludeExtraction)
          .sort((a, b) => getCreatedMs(b) - getCreatedMs(a));

        if (!cancelled) {
          setExtractions(completed);
        }
      } catch (err) {
        console.error("Failed to load ProCeding extractions:", err);

        if (!cancelled) {
          setError(err.message || "Failed to load ProCeding data.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadExtractions();

    return () => {
      cancelled = true;
    };
  }, []);

  const schemeRows = useMemo(() => {
    const rows = extractions.flatMap(buildSchemeRowsFromExtraction);

    // The first 10 analyses per adviser are free, regardless of whether they
    // would otherwise meet the charging threshold.
    const sorted = [...rows].sort((a, b) => a.createdMs - b.createdMs);
    const adviserFreeCount = new Map();
    const freeIds = new Set();

    for (const row of sorted) {
      const adviser = normalise(row.ownerEmail);
      const currentFreeCount = adviserFreeCount.get(adviser) || 0;

      if (currentFreeCount < FREE_ANALYSES_COUNT) {
        freeIds.add(row.id);
        adviserFreeCount.set(adviser, currentFreeCount + 1);
      }
    }

    return rows.map((row) =>
      freeIds.has(row.id)
        ? { ...row, charge: 0, chargeable: false, free: true }
        : row,
    );
  }, [extractions]);

  const filteredRows = useMemo(() => {
    const normalisedEmail = normalise(adviserEmail);
    return schemeRows.filter(
      (row) =>
        row.ownerEmail === normalisedEmail &&
        !row.ownerEmail.endsWith("@rosemountfs.co.uk"),
    );
  }, [schemeRows, adviserEmail]);

  const monthlyData = useMemo(() => {
    const map = new Map();

    filteredRows.forEach((row) => {
      if (!map.has(row.month)) {
        map.set(row.month, {
          month: row.month,
          createdMs: row.createdMs,
          cases: [],
        });
      }

      const month = map.get(row.month);
      month.cases.push(row);
    });

    return Array.from(map.values()).sort((a, b) => b.createdMs - a.createdMs);
  }, [filteredRows]);

  if (loading) {
    return <div className="ParaplanningTableState">Loading ProCeding data...</div>;
  }

  if (error) {
    return (
      <div className="ProCedingAdviserTab" style={{ color: "red" }}>
        {error}
      </div>
    );
  }

  return (
    <div className="ProCedingAdviserTab">
      {monthlyData.length === 0 ? (
        <div className="ParaplanningTableState">
          No ProCeding statements found.
        </div>
      ) : (
        <div>
          {monthlyData.map((month) => (
            <div key={month.month} style={{ marginBottom: "30px" }}>
              <h3
                style={{
                  marginBottom: "10px",
                  fontSize: "14px",
                  fontWeight: "600",
                }}
              >
                {month.month}
              </h3>

              <div className="ParaplanningTableWrap">
                <table className="ParaplanningTable">
                  <thead>
                    <tr>
                      {["", "Date", "Client", "Charged"].map((label) => (
                        <th key={label}>
                          <span
                            className={`ParaplanningTableHeaderButton ${
                              label === "Charged"
                                ? "ParaplanningTableHeaderButton--Right"
                                : ""
                            }`}
                          >
                            <span className="FP_Content">{label}</span>
                          </span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {month.cases.map((caseItem, index) => (
                      <tr key={caseItem.id}>
                        <td>
                          <div className="ParaplanningTableCell">
                            <span className="ParaplanningTableCell__Main FP_Content black">
                              {index + 1}.
                            </span>
                          </div>
                        </td>
                        <td>
                          <div className="ParaplanningTableCell">
                            <span className="ParaplanningTableCell__Main FP_Content black">
                              {formatDate(caseItem.createdMs)}
                            </span>
                          </div>
                        </td>
                        <td>
                          <div className="ParaplanningTableCell">
                            <span className="ParaplanningTableCell__Main FP_Content black">
                              {caseItem.clientName}
                            </span>
                          </div>
                        </td>
                        <td>
                          <div className="ParaplanningTableActions">
                            <span
                              className={`ParaplanningTableStatus ${
                                caseItem.chargeable
                                  ? "ParaplanningTableStatus--Cancelled"
                                  : "ParaplanningTableStatus--Completed"
                              }`}
                            >
                              {caseItem.chargeable ? "Yes" : "No"}
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProCedingAdviserTab;
